import {
  Box,
  Container,
  Paper,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Table,
  TableBody,
  TextField,
  Button,
  TablePagination,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import NavigationHeader from "../../components/NavigationHeader";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loading } from "../../components/Loading";
import { getValidToken } from "../../utils/authUtils";
import { formatDate, formatKms } from "../../utils/utils";
import {
  commonButtonProps,
  commonDateFieldProps,
  tableCellStyles,
  tableHeaderStyles,
} from "../../utils/styles";

const VehicleOdometerReadings = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const [odometerReadings, setOdometerReadings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    fetchOdometerReadings();
  }, [vehicleId, fromDate, toDate, page, limit]);

  const fetchOdometerReadings = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();

      // Build query parameters
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const queryString = params.toString();
      const url = `${
        process.env.REACT_APP_API_HOST
      }vehicles/odometer-readings/${vehicleId}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.isSuccess) {
        setOdometerReadings(response?.data?.data || []);
        setTotalCount(response?.data?.pagination?.totalCount || 0);
        setTotalPages(response?.data?.pagination?.totalPages || 0);
      } else {
        setOdometerReadings([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      setOdometerReadings([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleLimitChange = (event: any) => {
    setLimit(parseInt(event.target.value));
    setPage(1); // Reset to first page when changing limit
  };

  if (isLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title="Vehicle Odometer Readings"
        breadcrumbs={[
          { label: "Vehicles", path: "/vehicles" },
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          { label: "Vehicle Odometer Readings" },
        ]}
      />

      {/* Date Filter Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              {...commonDateFieldProps}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              {...commonDateFieldProps}
              fullWidth
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!fromDate && !toDate}
              {...commonButtonProps}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableHeaderStyles }}>Reading Date</TableCell>
              <TableCell sx={{ ...tableHeaderStyles }}>
                Odometer Reading (Kms)
              </TableCell>
              <TableCell sx={{ ...tableHeaderStyles }}>Context</TableCell>
              <TableCell sx={{ ...tableHeaderStyles }}>Created By</TableCell>
              <TableCell sx={{ ...tableHeaderStyles }}>Updated By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {odometerReadings.length > 0 ? (
              odometerReadings.map((reading) => (
                <TableRow key={reading.id}>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {formatDate(reading.readingDate)}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {formatKms(reading.odometerReading)}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {reading.context?.split("_").join(" ") || "-"}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {reading?.creator?.name || "-"}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles }}>
                    {reading?.updater?.name || "-"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No odometer readings found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              px: 2,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={limit}
              page={page - 1}
              onPageChange={(event: any, newPage: number) =>
                handlePageChange(event, newPage)
              }
              onRowsPerPageChange={(event) => handleLimitChange(event)}
            />
          </Box>
        )}
      </TableContainer>
    </Container>
  );
};

export default VehicleOdometerReadings;
